import { config } from '../config';

// MediaAsset mirrors the JSON shape returned by the per-org media library
// (internal/services/media). The wizard + public-profile form upload
// images here, then save the returned object_key onto the org via
// updatePublicProfile (logo_object_key / cover_image_object_key).
//
// Public read URL is derived server-side; the SPA uses publicURL() to
// build the same path for previews so we don't have to round-trip just
// to display what was uploaded.
export interface MediaAsset {
  id: string;
  kind: string;
  object_key: string;
  content_type: string;
  byte_size: number;
  width?: number;
  height?: number;
  status: 'processing' | 'ready' | 'failed';
  variants: Record<string, string>;
  uploaded_by: string;
  alt: string;
  caption: string;
  created_at: string;
}

// uploadMedia POSTs a single file via multipart/form-data to the per-org
// media library. The server detects content type from the bytes (CLAUDE.md
// security rule) and rejects unsupported types. Caller should size-check
// + MIME-check first as UX nicety; the server enforces both.
export async function uploadMedia(
  token: string | null,
  slug: string,
  file: File,
  opts: { alt?: string; caption?: string } = {},
): Promise<MediaAsset> {
  const form = new FormData();
  form.append('file', file);
  if (opts.alt) form.append('alt', opts.alt);
  if (opts.caption) form.append('caption', opts.caption);

  // Hand-rolled fetch keeps the multipart boundary intact — apiFetch
  // forces application/json and would corrupt the upload.
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/media`,
    { method: 'POST', headers, body: form },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`upload failed: ${res.status} ${text}`);
  }
  return (await res.json()) as MediaAsset;
}

// publicURL converts a media object_key (as stored on cms_block content,
// org branding, etc.) to a fetchable URL for SPA previews. Mirrors the
// server-side objectKeyToPublicURL — both prefix the key with config.cdnBase
// so dev (`${apiURL}/storage`) and prod (CDN) work transparently.
export function publicURL(objectKey: string | undefined | null): string {
  if (!objectKey) return '';
  // Seed content (e.g. partner logos from website-stock) stores fully-qualified
  // CDN URLs in fields that normally hold an object_key. Pass those through so
  // we don't double-prefix into a broken src.
  if (/^https?:\/\//i.test(objectKey)) return objectKey;
  return `${config.cdnBase}/${objectKey}`;
}

// MediaSize is the variant key written by the resize worker into
// MediaAsset.variants. Match the VariantKey constants in
// internal/services/media/resize.go.
export type MediaSize = 'small' | 'medium' | 'large';

// mediaURL is the size-aware companion to publicURL. Pass an asset
// and a desired size; you get the variant URL when the resize worker
// has produced one, otherwise it falls back to the original (which is
// always usable, just not size-optimised). Use this everywhere an
// <img> renders an uploaded asset.
//
// Examples:
//   <img src={mediaURL(asset, 'small')} />    // list row thumbnail
//   <img src={mediaURL(asset, 'medium')} />   // card hero
//   <img src={mediaURL(asset, 'large')} />    // page hero / lightbox
//
// Pass `undefined` for size to always get the original.
export function mediaURL(asset: MediaAsset | null | undefined, size?: MediaSize): string {
  if (!asset) return '';
  if (size && asset.variants && asset.variants[size]) {
    return publicURL(asset.variants[size]);
  }
  return publicURL(asset.object_key);
}

const VARIANT_SUFFIX: Record<MediaSize, string> = { small: 'S', medium: 'M', large: 'L' };

// logoVariantURL derives the resize-worker variant URL for a bare logo
// object_key (org/team logos store only the original key, not a full
// MediaAsset, so mediaURL() can't be used). The worker writes variants
// next to the original as "<orgID>/<hash>.<S|M|L>.webp" — see
// internal/services/media/resize.go variantObjectKey — so we swap the
// trailing ".O.<ext>" for ".<suffix>.webp".
//
// Returns '' when the key isn't the hashed "<...>.O.<ext>" shape (legacy
// "org/<id>/<uuid>" keys, already-resolved http(s) URLs) OR when the
// variant simply may not exist yet (seeded logos never went through the
// worker; a fresh upload's variant lands ~30s later). Callers MUST render
// the result with an <img onError> fallback to publicURL(key) — LogoImg
// does this — so a missing variant degrades silently to the original.
export function logoVariantURL(objectKey: string | undefined | null, size: MediaSize): string {
  if (!objectKey) return '';
  if (/^https?:\/\//i.test(objectKey)) return '';
  const m = /^(.*)\.O\.[a-z0-9]+$/i.exec(objectKey);
  if (!m) return '';
  return publicURL(`${m[1]}.${VARIANT_SUFFIX[size]}.webp`);
}

